<template>
  <article class="five-sharp">
    <div v-if="state === FiveSharpState.TITLE_SCREEN" class="title-screen">
      <Question
        v-if="exampleQuestion"
        ref="exampleQuestion"
        class="example-question"
        :data="exampleQuestion"
        :example="true"
      />
      <div v-else class="loading-animation-wrapper">
        <LoadingAnimation />
      </div>
      <div class="start-button-wrapper">
        <ThreeDButton class="start-button" icon="play" @click="startTraining">
          {{ $t('components.five-sharp.start-button') }}
        </ThreeDButton>
      </div>
    </div>
    <template v-else-if="state === FiveSharpState.TRAINING_IN_PROGRESS">
      <QuestionListManager
        v-if="testQuestions.length > 0"
        :user-answer-model="UserAnswerModel.TEST5SHARP"
        :session-id="testSessionId"
        :instant-feedback="true"
        :is-homework="false"
        :finished-callback="finishedCallback"
      />
      <div v-else class="loading-animation-wrapper">
        <LoadingAnimation />
      </div>
    </template>
    <template v-else-if="state === FiveSharpState.FINISHED">
      <QuestionListEvaluation
        ref="questionListEvaluation"
        :user-answer-model="UserAnswerModel.TEST5SHARP"
        :session-id="testSessionId"
      />
      <div class="retry-button-wrapper">
        <ThreeDButton class="retry-button" icon="redo" @click="startTraining">
          {{ $t('components.five-sharp.retry-button') }}
        </ThreeDButton>
      </div>
    </template>
  </article>
</template>

<script>
import LoadingAnimation from '../LoadingAnimation'
import Question from '../Question'
import QuestionListEvaluation from '@/components/training/QuestionListEvaluation'
import QuestionListManager from '../QuestionListManager'
import ThreeDButton from '../ThreeDButton'
import { getExampleQuestion } from '@/apis/training'
import { AuthStoreNamespacedTypes } from '@/store/auth'
import { QuestionStoreNamespacedTypes } from '@/store/questions'
import { UserAnswerModel } from '@/enums'
import { typesetMathMl } from '@/utils/mathml'

/**
 * @enum {string}
 */
const FiveSharpState = {
  TITLE_SCREEN: 'title-screen',
  TRAINING_IN_PROGRESS: 'training-in-progress',
  FINISHED: 'finished',
}

export default {
  name: 'FiveSharp',
  components: {
    LoadingAnimation,
    Question,
    QuestionListEvaluation,
    QuestionListManager,
    ThreeDButton,
  },
  props: {
    /**
     * The ID of the lesson to base the training session on.
     */
    lessonId: {
      type: Number,
      required: true,
    },
    classLevel: {
      type: Number,
      default: undefined,
    },
  },
  data() {
    return {
      FiveSharpState,
      UserAnswerModel,
      /**
       * The current state of the component and its session.
       */
      state: FiveSharpState.TITLE_SCREEN,

      /**
       * Data for the example question displayed before starting the session.
       */
      exampleQuestion: null,

      /**
       * The ID of the current test session.
       */
      testSessionId: null,
    }
  },
  computed: {
    /**
     * The class level of the current user.
     * @returns {number}
     */
    userClass() {
      return this.$store.getters[
        AuthStoreNamespacedTypes.getters.GET_USER_CLASS
      ]()
    },

    /**
     * Question data for the session.
     */
    testQuestions() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTIONS
      ](UserAnswerModel.TEST5SHARP, this.testSessionId)
    },
  },
  methods: {
    /**
     * Changes the view to the finished state.
     */
    finishedCallback() {
      this.state = FiveSharpState.FINISHED

      this.$emit('finished')

      this.$store
        .dispatch(QuestionStoreNamespacedTypes.actions.FETCH_RESULT, {
          sessionId: this.testSessionId,
          userAnswerModel: UserAnswerModel.TEST5SHARP,
        })
        .finally(() => {
          // Scroll to the evaluation component once rendered
          setTimeout(() => {
            this.$refs.questionListEvaluation.$el.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            })
          })
        })
    },

    /**
     * Calls the API to start a test session and applies the test data.
     */
    startTraining() {
      this.state = FiveSharpState.TRAINING_IN_PROGRESS

      // Clear any existing test session ID, to make sure we're not displaying old data while loading the new data
      this.testSessionId = null

      this.$store
        .dispatch(QuestionStoreNamespacedTypes.actions.INITIALIZE_FIVE_SHARP, {
          lessonId: this.lessonId,
          classLevel:
            this.classLevel !== undefined ? this.classLevel : this.userClass,
        })
        .then(sessionId => {
          this.testSessionId = sessionId
        })
    },
  },
  watch: {
    /**
     * Updates the example question and clears the test data whenever the lesson ID changes.
     */
    lessonId: {
      handler() {
        this.state = FiveSharpState.TITLE_SCREEN

        this.exampleQuestion = null
        this.testSessionId = null

        getExampleQuestion(this.lessonId).then(exampleQuestion => {
          this.exampleQuestion = exampleQuestion

          // Typeset MathML in the question once the DOM has been updated
          this.$nextTick(() => {
            typesetMathMl(this.$refs.exampleQuestion.$el)
          })
        })
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.five-sharp {
  position: relative;
}

.example-question {
  margin: auto;
  opacity: 0.5;
  filter: blur(0.1em);
}

.start-button-wrapper {
  position: absolute;
  left: 50%;
  top: 50%;
  font-size: 2em;
  transform: translate(-50%, -50%);
}

.loading-animation-wrapper {
  padding: 2em;
  text-align: center;
  font-size: 3em;
}

.retry-button-wrapper {
  font-size: 1.5em;
  text-align: center;
  padding: var(--content-padding-large);
}
</style>
